import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, getAccess, getRefresh, setTokens, clearTokens, apiError } from "@/lib/api";

const AuthCtx = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const WS_URL = BACKEND_URL.replace(/^http/, "ws");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const wsRetryRef = useRef(0);
  const wsClosedByUs = useRef(false);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/profile");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Live balance feed — opens a WebSocket whenever we have a valid access
  // token and a logged-in user. Pushes balance updates straight into the
  // context so every consumer that reads `user.points` re-renders.
  // -----------------------------------------------------------------------
  const closeWs = useCallback(() => {
    wsClosedByUs.current = true;
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
  }, []);

  const openWs = useCallback(() => {
    if (!WS_URL) return;
    const token = getAccess();
    if (!token) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    wsClosedByUs.current = false;
    const ws = new WebSocket(`${WS_URL}/api/ws/economy?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "balance" && typeof msg.balance === "number") {
          setUser((prev) => (prev ? { ...prev, points: msg.balance } : prev));
        } else if (msg.type === "transfer_received") {
          // ToastProvider lives below AuthProvider in the tree, so we
          // can't `useToast()` here. Dispatch a window event instead;
          // Layout (which sits under ToastProvider) listens and surfaces
          // a success toast on the recipient's screen in real time.
          try {
            window.dispatchEvent(new CustomEvent("chillets:transfer-received", {
              detail: {
                from_name: msg.from_name || "Someone",
                amount: Number(msg.amount) || 0,
              },
            }));
          } catch { /* noop in non-browser env */ }
        } else if (msg.type === "cooldown_changed" || msg.type === "cooldown_cleared") {
          // Admin edited a cooldown duration (global) OR cleared a
          // single user's row. Any page with an active countdown
          // listens for `chillets:cooldown-update` and refetches its
          // own cooldown row — no manual refresh needed.
          try {
            window.dispatchEvent(new CustomEvent("chillets:cooldown-update", {
              detail: { type: msg.type, key: msg.key, seconds: msg.seconds },
            }));
          } catch { /* noop in non-browser env */ }
        }
      } catch { /* ignore malformed frames */ }
    };
    ws.onopen = () => { wsRetryRef.current = 0; };
    ws.onclose = () => {
      wsRef.current = null;
      if (wsClosedByUs.current) return;
      // Exponential-ish backoff up to 30s
      const delay = Math.min(30000, 1000 * 2 ** wsRetryRef.current++);
      setTimeout(() => {
        if (!wsClosedByUs.current && getAccess()) openWs();
      }, delay);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
  }, []);

  // Open/close the WS in lockstep with the user's auth state.
  // We deliberately key off `user?.id` (a stable string) rather than `user`
  // so balance pushes — which mutate the user object — don't churn the
  // socket and trigger an infinite re-connect loop.
  const userId = user?.id;
  useEffect(() => {
    if (userId && getAccess()) openWs();
    else closeWs();
    return () => closeWs();
  }, [userId, openWs, closeWs]);

  useEffect(() => {
    if (getAccess()) fetchProfile();
    else setLoading(false);
  }, [fetchProfile]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data.access_token, data.refresh_token);
    await fetchProfile();
    return data;
  };

  const register = async (email, username, password) => {
    const { data } = await api.post("/auth/register", { email, username, password });
    setTokens(data.access_token, data.refresh_token);
    await fetchProfile();
    return data;
  };

  const adminLogin = async (username, password) => {
    const { data } = await api.post("/auth/admin/login", { username, password });
    setTokens(data.access_token, data.refresh_token);
    await fetchProfile();
    return data;
  };

  const consumeOauthCallback = async (access, refresh) => {
    setTokens(access, refresh);
    await fetchProfile();
  };

  const logout = async () => {
    // Best-effort: tell the server to invalidate the refresh token, but always
    // tear down local state — even if the network call fails — so the user is
    // logged out from the UI no matter what.
    try {
      const r = getRefresh();
      if (r) await api.post("/auth/logout", { refresh_token: r });
    } catch (e) {
      console.warn("logout refresh-revoke failed (continuing)", e);
    }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, adminLogin, consumeOauthCallback, logout, refreshProfile: fetchProfile, apiError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
